import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageCircle,
  User,
  Calendar,
  Star,
  TrendingUp,
  Activity,
  Users2,
  ArrowRight,
  Filter,
  Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FeedbackHistory = ({ employeeId, employeeName }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'given', 'received'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', '360', 'continuo', 'performance', 'cultural'

  useEffect(() => {
    if (employeeId) {
      loadFeedbackHistory();
    }
  }, [employeeId, filter, typeFilter]);

  const loadFeedbackHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/api/feedback/employee/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          filter: filter,
          type: typeFilter
        }
      });
      
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico de feedback:', error);
      // Mock data para desenvolvimento
      setFeedbacks([
        {
          id: '1',
          type: '360',
          feedback_giver_id: 'other1',
          feedback_giver_name: 'Maria Silva',
          feedback_receiver_id: employeeId,
          feedback_receiver_name: employeeName,
          cycle_name: 'Avaliação 360° - Q4 2024',
          rating: 4.5,
          created_at: '2024-11-15T10:00:00Z',
          status: 'completed',
          role: 'received'
        },
        {
          id: '2',
          type: 'continuo',
          feedback_giver_id: employeeId,
          feedback_giver_name: employeeName,
          feedback_receiver_id: 'other2',
          feedback_receiver_name: 'João Santos',
          message: 'Excelente trabalho na apresentação do projeto',
          rating: 5,
          created_at: '2024-11-10T14:30:00Z',
          status: 'completed',
          role: 'given'
        },
        {
          id: '3',
          type: 'performance',
          feedback_giver_id: 'manager1',
          feedback_giver_name: 'Ana Costa',
          feedback_receiver_id: employeeId,
          feedback_receiver_name: employeeName,
          cycle_name: 'Avaliação de Performance - Nov 2024',
          rating: 4.2,
          created_at: '2024-11-05T09:15:00Z',
          status: 'completed',
          role: 'received'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case '360': return <Users2 className="w-4 h-4" />;
      case 'continuo': return <Activity className="w-4 h-4" />;
      case 'performance': return <TrendingUp className="w-4 h-4" />;
      case 'cultural': return <Star className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case '360': return '360° Feedback';
      case 'continuo': return 'Feedback Contínuo';
      case 'performance': return 'Performance';
      case 'cultural': return 'Cultural';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case '360': return 'bg-blue-100 text-blue-800';
      case 'continuo': return 'bg-green-100 text-green-800';
      case 'performance': return 'bg-purple-100 text-purple-800';
      case 'cultural': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    return role === 'given' ? 'border-l-blue-400' : 'border-l-green-400';
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    if (filter === 'given' && feedback.role !== 'given') return false;
    if (filter === 'received' && feedback.role !== 'received') return false;
    if (typeFilter !== 'all' && feedback.type !== typeFilter) return false;
    return true;
  });

  if (!employeeId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p>Salve o funcionário primeiro para visualizar o histórico de feedback</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Todos</option>
              <option value="received">Recebidos</option>
              <option value="given">Aplicados</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Tipo:</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Todos</option>
              <option value="360">360°</option>
              <option value="continuo">Contínuo</option>
              <option value="performance">Performance</option>
              <option value="cultural">Cultural</option>
            </select>
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          {filteredFeedbacks.length} feedback{filteredFeedbacks.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Estatísticas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ArrowRight className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Aplicados</span>
          </div>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {feedbacks.filter(f => f.role === 'given').length}
          </p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ArrowRight className="w-4 h-4 text-green-600 transform rotate-180" />
            <span className="text-sm font-medium text-green-800">Recebidos</span>
          </div>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {feedbacks.filter(f => f.role === 'received').length}
          </p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Star className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Média</span>
          </div>
          <p className="text-2xl font-bold text-yellow-900 mt-1">
            {feedbacks.length > 0 
              ? (feedbacks.reduce((sum, f) => sum + (f.rating || 0), 0) / feedbacks.length).toFixed(1)
              : '0.0'
            }
          </p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">Este Mês</span>
          </div>
          <p className="text-2xl font-bold text-purple-900 mt-1">
            {feedbacks.filter(f => {
              const feedbackDate = new Date(f.created_at);
              const now = new Date();
              return feedbackDate.getMonth() === now.getMonth() && feedbackDate.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
      </div>

      {/* Lista de Feedbacks */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse border rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredFeedbacks.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 text-lg">Nenhum feedback encontrado</p>
          <p className="text-gray-400 text-sm mt-1">
            {filter === 'all' 
              ? 'Este funcionário ainda não deu ou recebeu feedbacks'
              : filter === 'given'
              ? 'Este funcionário ainda não deu feedbacks'
              : 'Este funcionário ainda não recebeu feedbacks'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFeedbacks.map((feedback) => (
            <div 
              key={feedback.id} 
              className={`border-l-4 ${getRoleColor(feedback.role)} bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(feedback.type)}`}>
                      {getTypeIcon(feedback.type)}
                      <span>{getTypeLabel(feedback.type)}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      {feedback.role === 'given' ? (
                        <>
                          <span className="font-medium text-blue-700">Dado para:</span>
                          <span>{feedback.feedback_receiver_name}</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-green-700">Recebido de:</span>
                          <span>{feedback.feedback_giver_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {feedback.cycle_name && (
                    <h4 className="font-medium text-gray-900 mb-1">{feedback.cycle_name}</h4>
                  )}
                  
                  {feedback.message && (
                    <p className="text-gray-700 mb-2">{feedback.message}</p>
                  )}
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(feedback.created_at).toLocaleString('pt-BR')}</span>
                    </div>
                    
                    {feedback.rating && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-medium">{feedback.rating}/5</span>
                      </div>
                    )}
                    
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      feedback.status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {feedback.status === 'completed' ? 'Completo' : 'Pendente'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackHistory;